import { useState, useMemo, useEffect } from 'react'
import { Tabs, TabsList, TabsTrigger } from '@/race-tracker/components/ui/tabs'
import { Lightbulb, Shield, Lock } from 'lucide-react'
import { useAuth } from '@/lib/AuthContext'
import { canAccessTab } from '@/lib/permissions'
import IdeaBoardTab from './IdeaBoardTab'
import IdeaBoardAdminTab from './IdeaBoardAdminTab'
import '@/big-political-list/bpl-theme.css'

const TABS = [
  { id: 'board', label: 'Idea Board', icon: Lightbulb },
  { id: 'admin', label: 'Idea Board Admin', icon: Shield },
]

export default function IdeaBoardPage() {
  const [activeTab, setActiveTab] = useState('board')
  const { canWritePage, rolePagePermissions, isSuperAdmin } = useAuth()
  const canWriteAdmin = canWritePage('idea-board:admin')

  const visibleTabs = useMemo(
    () => TABS.filter((t) => canAccessTab('idea-board', t.id, rolePagePermissions, isSuperAdmin)),
    [rolePagePermissions, isSuperAdmin],
  )

  useEffect(() => {
    if (visibleTabs.length === 0) return
    if (!visibleTabs.find((t) => t.id === activeTab)) {
      setActiveTab(visibleTabs[0].id)
    }
  }, [visibleTabs, activeTab])

  const hasAdminTab = visibleTabs.some((t) => t.id === 'admin')

  return (
    <div className="bpl-app min-h-full bg-background text-foreground">
      <header className="bg-navy text-white px-6 py-4">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Lightbulb className="w-4 h-4 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight">Idea Board</h1>
            </div>
          </div>
        </div>
      </header>

      <div className="bg-card border-b sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="h-11 bg-transparent border-0 gap-0 p-0 rounded-none">
              {visibleTabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className="h-11 rounded-none border-b-2 border-transparent px-4 text-xs font-medium text-muted-foreground data-[state=active]:text-foreground data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=active]:bg-transparent"
                  >
                    <Icon className="w-3.5 h-3.5 mr-1.5" />
                    {tab.label}
                  </TabsTrigger>
                )
              })}
            </TabsList>
          </Tabs>
        </div>
      </div>

      <main className="max-w-[1600px] mx-auto p-5">
        {activeTab === 'board' && <IdeaBoardTab />}
        {activeTab === 'admin' &&
          (hasAdminTab ? (
            <IdeaBoardAdminTab canWrite={canWriteAdmin} />
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-muted-foreground" />
              </div>
              <h3 className="font-semibold text-foreground mb-1">Admin Access Required</h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                This section is restricted. Contact your system administrator if you need access.
              </p>
            </div>
          ))}
      </main>
    </div>
  )
}
